// Symptom score severity tiers — see DESIGN_SYSTEM.md Section 2
// ("Severity Colors — Symptom Scores ONLY") for the exact ranges/hex values.
// Listed worst-to-best to match how the breakdown is shown to patients.
export const SEVERITY_TIERS = [
  {
    min: 1,
    max: 29,
    label: "Severely Bothersome",
    textColor: "#EF4444",
    bgColor: "#FEE2E2",
    borderColor: "#FCA5A5",
    description:
      "Your symptoms are likely significantly interfering with your ability to perform activities such as " +
      "sleeping, socializing, working, or running errands. They may be causing substantial distress and " +
      "affecting your overall well-being.",
    guidance:
      "Your symptoms are having a serious impact on your daily life. Message your care team so they can " +
      "review your treatment plan with you.",
  },
  {
    min: 30,
    max: 59,
    label: "Very Bothersome",
    textColor: "#F97316",
    bgColor: "#FFEDD5",
    borderColor: "#FDBA74",
    description:
      "Your symptoms likely have a large impact on your ability to carry out daily activities such as sleeping, " +
      "socializing, working, or running errands. They may be causing considerable discomfort and affecting your " +
      "overall well-being.",
    guidance:
      "Your symptoms are having a large impact on your daily life. Consider messaging your care team before " +
      "your next appointment.",
  },
  {
    min: 60,
    max: 89,
    label: "Moderately Bothersome",
    textColor: "#EAB308",
    bgColor: "#FEF9C3",
    borderColor: "#FDE047",
    description:
      "Your symptoms are likely causing notable disruption in your daily life. You are able to perform daily " +
      "activities such as sleeping, socializing, working, or running errands, but with some difficulty or " +
      "discomfort.",
    guidance:
      "Your symptoms are manageable but still disruptive. Keep following your care plan, and tell your care " +
      "team at your next visit what is bothering you most.",
  },
  {
    min: 90,
    // DESIGN_SYSTEM.md documents this tier as 90–99, but the score is presented
    // as "out of 100" and the progress ring computes score/100, so 100 is
    // reachable. Left at 99 it matched no tier at all. Worth confirming in the
    // design doc, which does not currently define 0 or 100.
    max: 100,
    label: "Mildly Bothersome",
    textColor: "#22C55E",
    bgColor: "#DCFCE7",
    borderColor: "#86EFAC",
    description:
      "Your symptoms are likely causing some level of discomfort and inconvenience, but they are not significantly " +
      "impacting your daily life. You may be aware of the symptoms but they are generally tolerable and do not " +
      "impact your ability to accomplish daily tasks such as sleeping, socializing, working, or running errands.",
    guidance:
      "Your symptoms are having little impact on your daily life. Keep following your current care plan.",
  },
];

// Shown when a score falls outside every tier — no assessment taken yet (the
// codebase uses 0 for "no score"), or a value the tiers don't cover.
//
// This deliberately does NOT fall back to a real tier. The previous fallback
// returned the last element of SEVERITY_TIERS, which is the mildest one, so an
// unrecognised score rendered as green "Mildly Bothersome" alongside copy telling
// the patient their symptoms were improving. An unknown score must never read as
// good news in a clinical context.
export const UNKNOWN_TIER = {
  min: null,
  max: null,
  label: "No score yet",
  textColor: "var(--youro-muted)",
  bgColor: "var(--youro-border)",
  borderColor: "var(--youro-stone)",
  description:
    "No symptom assessment has been recorded yet. Complete an assessment to see " +
    "how bothersome your symptoms are.",
  // No reassurance and no instruction when there is nothing to interpret.
  guidance: "",
};

// 0 is this codebase's "no score recorded" sentinel — PatientHomePage seeds
// state with it and tests `symptomScore !== 0` to decide whether a score exists.
// Centralised here so every screen agrees on what counts as having a score.
export const hasSymptomScore = (score) =>
  typeof score === "number" &&
  Number.isFinite(score) &&
  score >= 1 &&
  score <= 100;

export const getSeverityTier = (score) =>
  SEVERITY_TIERS.find((tier) => score >= tier.min && score <= tier.max) ||
  UNKNOWN_TIER;

export const getScoreColor = (score) => getSeverityTier(score).textColor;
export const getSeverityLabel = (score) => getSeverityTier(score).label;
